// The home cove (浦): the funaya row seen from the harbour. Little ones the
// player has saved float in the harbour; decorations built from beach finds
// line the seawall, and every one adds light to the cove.
import * as THREE from 'three';
import { DECOR_MAX, coveLight } from '../meta/profile.js';

// Harbour frame in world units (from the world GLB: funaya row along x = -45).
const SEAWALL_X = -41.5, Z0 = -24, Z1 = 6;
export const COVE_CAMERA = { target: new THREE.Vector3(-37, 0.5, -9), dist: 34, pitch: THREE.MathUtils.degToRad(24), yaw: THREE.MathUtils.degToRad(78), fov: 30 };

function lanternMesh(kagee) {
  const g = new THREE.Group();
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 1.4, 6), new THREE.MeshStandardMaterial({ color: kagee ? 0x050409 : 0x3b2a26, roughness: 1 }));
  post.position.y = 0.7;
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.2, 0.42, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.6, 0.85, 0.35) }));
  body.position.y = 1.55;
  const cap = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.2, 8), new THREE.MeshStandardMaterial({ color: kagee ? 0x050409 : 0x2a1e1a }));
  cap.position.y = 1.85;
  g.add(post, body, cap);
  return g;
}
function netFloatMesh(kagee) {
  const g = new THREE.Group();
  const pole = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.6, 1.8), new THREE.MeshStandardMaterial({ color: kagee ? 0x050409 : 0x4a3a30, transparent: true, opacity: 0.85 }));
  pole.position.y = 0.8;
  g.add(pole);
  const tints = [[0.3, 0.95, 0.8], [0.4, 0.7, 1.2], [0.9, 1.1, 0.5]];
  for (let k = 0; k < 3; k++) {
    const f = new THREE.Mesh(new THREE.SphereGeometry(0.17, 12, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(...tints[k]) }));
    f.position.set(0.1, 0.5 + (k % 2) * 0.5, -0.6 + k * 0.6);
    g.add(f);
  }
  return g;
}
function norenMesh(kagee, i) {
  const colors = [[0.12, 0.2, 0.55], [0.6, 0.15, 0.12], [0.1, 0.35, 0.25], [0.5, 0.42, 0.15]];
  const m = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1.0, 3, 1), new THREE.MeshBasicMaterial({ color: new THREE.Color(...colors[i % 4]).multiplyScalar(kagee ? 1.4 : 1), side: THREE.DoubleSide }));
  m.rotation.y = Math.PI / 2;
  return m;
}
function boatMesh(kagee) {
  const hull = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.25, 3.2, 8, 1, false, 0, Math.PI).rotateZ(Math.PI / 2).rotateY(Math.PI / 2), new THREE.MeshStandardMaterial({ color: kagee ? 0x050409 : 0x5a3f32, side: THREE.DoubleSide }));
  const light = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.8, 1.9, 1.5) }));
  light.position.set(0, 0.9, 0.8);
  const g = new THREE.Group(); g.add(hull, light);
  return g;
}

export class CoveView {
  constructor(stage, bank, profile, kagee) {
    this.stage = stage;
    this.group = new THREE.Group();
    stage.scene.add(this.group);
    this.kagee = kagee;
    this.actors = [];
    this.lights = [];
    this.t = 0;
    this.rebuild(profile, bank);
  }

  rebuild(profile, bank) {
    for (const a of this.actors) a.dispose?.();
    this.group.clear();
    this.actors = [];
    for (const l of this.lights) this.stage.scene.remove(l);
    this.lights = [];
    const seaY = this.stage.sea.position.y;
    const zAt = (k, n) => Z0 + ((Z1 - Z0) * (k + 0.5)) / n;
    const d = profile.decor;
    for (let k = 0; k < d.lantern; k++) {
      const m = lanternMesh(this.kagee); m.position.set(SEAWALL_X + 0.6, 0.6, zAt(k, DECOR_MAX.lantern)); this.group.add(m);
      const l = new THREE.PointLight(0xffa850, 3, 7, 1.6); l.position.set(m.position.x + 0.3, 2.1, m.position.z); this.stage.scene.add(l); this.lights.push(l);
    }
    for (let k = 0; k < d.netfloat; k++) { const m = netFloatMesh(this.kagee); m.position.set(SEAWALL_X - 1.2, 0.6, zAt(k, DECOR_MAX.netfloat) + 1.2); this.group.add(m); }
    for (let k = 0; k < d.noren; k++) { const m = norenMesh(this.kagee, k); m.position.set(-43.6, 1.6, -25.4 + k * 7.6); this.group.add(m); }
    for (let k = 0; k < d.boat; k++) { const m = boatMesh(this.kagee); m.position.set(-34 + (k % 2) * 3, seaY + 0.25, -20 + k * 8); m.userData.bob = k; this.group.add(m); }
    // Residents: up to 24 little ones drawn, in proportion to what was saved.
    const kinds = Object.entries(profile.residents).filter(([, n]) => n > 0);
    const total = kinds.reduce((n, [, v]) => n + v, 0);
    let placed = 0;
    for (const [kind, n] of kinds) {
      const show = Math.max(1, Math.round((n / total) * Math.min(24, total)));
      for (let k = 0; k < show && placed < 24; k++, placed++) {
        const a = bank.actor(kind);
        a.play?.('idle');
        const ang = placed * 2.399, rad = 2 + (placed % 6) * 1.1;
        a.group.position.set(-33 + Math.cos(ang) * rad * 0.7, seaY + 0.05, -9 + Math.sin(ang) * rad * 1.6);
        a.home = a.group.position.clone();
        a.phase = placed * 0.7;
        this.group.add(a.group);
        this.actors.push(a);
      }
    }
    this.light = coveLight(profile);
  }

  update(dt, camera) {
    this.t += dt;
    for (const a of this.actors) {
      a.group.position.x = a.home.x + Math.sin(this.t * 0.3 + a.phase) * 0.8;
      a.group.position.z = a.home.z + Math.cos(this.t * 0.25 + a.phase) * 0.6;
      a.flip = Math.cos(this.t * 0.3 + a.phase) < 0;
      a.update(dt, camera);
    }
    for (const c of this.group.children) if (c.userData.bob !== undefined) c.position.y = this.stage.sea.position.y + 0.25 + Math.sin(this.t * 1.3 + c.userData.bob) * 0.05;
    this.lights.forEach((l, i) => { l.intensity = 3 * (0.9 + 0.1 * Math.sin(this.t * 6 + i)); });
  }

  dispose() {
    for (const a of this.actors) a.dispose?.();
    for (const l of this.lights) this.stage.scene.remove(l);
    this.stage.scene.remove(this.group);
  }
}
